import { TFunction } from 'i18next'
import { DateTime, Duration } from 'luxon'
import { DataDrivenPropertyValueSpecification, ExpressionSpecification, LngLatBounds } from 'maplibre-gl'
import { useEffect, useState } from 'react'
import { Leg, LineStringTrip, Arrival } from 'src/generated/brezavta/requests/types.gen'

export type FormatType = 'absolute' | 'relative'

function formatAbsoluteSeconds(seconds_today: number): string {
    return DateTime.local({ zone: 'Europe/Ljubljana' }).set({ hour: 0, minute: 0, second: 0 }).plus({ seconds: seconds_today }).toFormat('HH:mm')
}

function getSecondsInDay(dt: DateTime): number {
    return parseInt(dt.toFormat('HH')) * 60 * 60 + parseInt(dt.toFormat('mm')) * 60 + parseInt(dt.toFormat('ss'))
}

/**
 * Formats the time based on the given parameters.
 *
 * If the time is far in the future (more than an hour), the time is formatted as an absolute time.
 * If the time is in the past, the time is formatted as 'Arrived' or 'Departed'.
 * If the time is under a minute, the time is formatted as 'Arriving'.
 *
 * @param {number} seconds_arrival - The arrival time in seconds.
 * @param {number} seconds_departure - The departure time in seconds.
 * @param {FormatType} format - The format type, either 'absolute' or another format.
 * @param {TFunction} t - The translation function.
 * @param {"final" | "first"} [type] - Optional parameter to specify if the time is for the final or first event.
 *
 * @returns {string} - The formatted time string.
 */
export function formatTime(seconds_arrival: number, seconds_departure: number, format: FormatType, t: TFunction, type?: 'final' | 'first') {
    if (format === 'absolute')
        return formatAbsoluteSeconds(seconds_arrival)

    const now = getSecondsInDay(DateTime.local({ zone: 'Europe/Ljubljana' }))
    const timeUntilArrival = seconds_arrival - now
    // Times far in the future should be absolute
    if (timeUntilArrival > 3600) {
        return formatAbsoluteSeconds(seconds_arrival)
    }

    // Currently in station
    if (now > seconds_arrival && now < seconds_departure) {
        return t('At station')
    }

    // Already departed
    if (now > seconds_departure) {
        return type == 'final' ? t('Arrived') : t('Departed')
    }

    // Under a minute
    if (timeUntilArrival < 60) {
        return type == 'first' ? `${timeUntilArrival} s` : t('Arriving')
    }

    return `${Math.floor(timeUntilArrival / 60)} min`
}

/**
 * Formats a duration in milliseconds into a human-readable string.
 *
 * @param duration_millis - The duration to format, in milliseconds.
 * @returns A string representation of the duration.
 *          For durations less than an hour, it returns the number of minutes followed by 'min'.
 *          For durations of an hour or more, it returns hours and minutes in the format 'Xh Ymin'.
 */
export function formatDuration(duration_millis: number) {
    const duration = Duration.fromMillis(duration_millis).rescale()
    if (duration_millis < 60 * 60 * 1000)
        return duration.toFormat('m') + 'min'
    else
        return duration.get('hours') + 'h ' + duration.get('minutes') + 'min'
}

export function getBoundsForPolyline(polyline: LineStringTrip) {
    return polyline.coordinates.reduce(function (bounds: LngLatBounds, coord: number[]) {
        return bounds.extend(coord as [number, number])
    }, new LngLatBounds(polyline.coordinates[0] as [number, number], polyline.coordinates[0] as [number, number]))
}

// Function to add random error to coordinates
export function addRandomError(coord: number) {
    const randomFactor = 0.00007 // Adjust this value to set the amount of error
    return coord + (Math.random() - 0.5) * randomFactor
}

export function useSimpleCache<T>(data: T, localStorageKey: string, default_val: T): T {
    const [stops, setStops] = useState<T>(() => {
        const storedStops = localStorage.getItem(localStorageKey)
        if (storedStops) {
            return JSON.parse(storedStops)
        }
        return default_val
    })

    useEffect(() => {
        if (data) {
            // eslint-disable-next-line react-hooks/set-state-in-effect
            setStops(data)
            localStorage.setItem(localStorageKey, JSON.stringify(data))
        }
    }, [data, localStorageKey])
    return stops
}

/**
 * Mouse pointer
 * @param map Maplibre instance
 * @param layerId Layer ID
 */
export function bindCursorPointer(map, layerId) {
    const enterHandler = () => {
        map.current.getCanvas().style.cursor = 'pointer'
    }
    const leaveHandler = () => {
        map.current.getCanvas().style.cursor = ''
    }
    map.current.on('mouseenter', layerId, enterHandler)
    map.current.on('mouseleave', layerId, leaveHandler)
    return () => {
        if (!map.current) return
        map.current.off('mouseenter', layerId, enterHandler)
        map.current.off('mouseleave', layerId, leaveHandler)
    }
}

export function namespaceIconExpression(expression: ExpressionSpecification): DataDrivenPropertyValueSpecification<string> {
    return ['concat', 'ojpp:', expression]
}

export function calculateBoundsForLegs(legs: Leg[]) {
    const bounds = [[180, 90], [-180, -90]]
    legs.forEach((leg) => {
        leg.geometry.coordinates.forEach((coord) => {
            bounds[0][0] = Math.min(bounds[0][0], coord[0])
            bounds[0][1] = Math.min(bounds[0][1], coord[1])
            bounds[1][0] = Math.max(bounds[1][0], coord[0])
            bounds[1][1] = Math.max(bounds[1][1], coord[1])
        })
    })
    return bounds
}

let _vehicleSvgTemplate: string | null = null

export async function fetchVehicleSvg(): Promise<string> {
    if (_vehicleSvgTemplate) return _vehicleSvgTemplate
    const basePath = import.meta.env.VITE_BASE_PATH || ''
    const url = `${window.location.origin}${basePath}/icons/vehicle.svg`
    const resp = await fetch(url)
    _vehicleSvgTemplate = await resp.text()
    return _vehicleSvgTemplate
}

function normalizeVehicleHex(hex: string): string {
    let value = hex.trim().toLowerCase()
    if (!value.startsWith('#')) value = `#${value}`
    if (/^#[0-9a-f]{3}$/i.test(value)) value = '#' + value.slice(1).split('').map(c => c + c).join('')
    return value
}

export function normalizeVehicleColor(color?: string | null): string {
    if (!color) return '#666666'
    const trimmed = color.trim()
    if (/^#[0-9a-f]{3}([0-9a-f]{3})?$/i.test(trimmed)) return trimmed
    if (/^[0-9a-f]{3}([0-9a-f]{3})?$/i.test(trimmed)) return `#${trimmed}`
    return '#666666'
}

function vehicleHexToRgb(hex: string) {
    const n = normalizeVehicleHex(hex)
    return {
        r: parseInt(n.slice(1, 3), 16),
        g: parseInt(n.slice(3, 5), 16),
        b: parseInt(n.slice(5, 7), 16),
    }
}

function vehicleRgbToHex(r: number, g: number, b: number) {
    return '#' + [r, g, b]
        .map(v => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, '0'))
        .join('')
}

function lightenColor(hex: string, amount: number) {
    const { r, g, b } = vehicleHexToRgb(hex)
    return vehicleRgbToHex(r + (255 - r) * amount, g + (255 - g) * amount, b + (255 - b) * amount)
}

export function darkenColor(hex: string, amount: number) {
    const { r, g, b } = vehicleHexToRgb(hex)
    return vehicleRgbToHex(r * (1 - amount), g * (1 - amount), b * (1 - amount))
}

function colorToImageId(color: string): string {
    return normalizeVehicleHex(color).replace('#', '')
}

export { colorToImageId }

function renderVehicleSvg(template: string, color: string): string {
    const main = normalizeVehicleHex(color)
    let svg = template
    svg = svg.replace(/__COLOR_MAIN__/g, main)
    svg = svg.replace(/__COLOR_LIGHT__/g, lightenColor(main, 0.12))
    svg = svg.replace(/__COLOR_DARK__/g, darkenColor(main, 0.3))
    return svg
}

const VEHICLE_IMAGE_WIDTH = 72
const VEHICLE_IMAGE_HEIGHT = 96

async function svgToImageData(svgString: string): Promise<ImageData> {
    const blob = new Blob([svgString], { type: 'image/svg+xml' })
    const url = URL.createObjectURL(blob)

    const img = new Image()
    img.crossOrigin = 'anonymous'
    await new Promise<void>((resolve, reject) => {
        img.onload = () => resolve()
        img.onerror = reject
        img.src = url
    })

    const canvas = document.createElement('canvas')
    canvas.width = VEHICLE_IMAGE_WIDTH
    canvas.height = VEHICLE_IMAGE_HEIGHT
    const ctx = canvas.getContext('2d')!
    ctx.clearRect(0, 0, VEHICLE_IMAGE_WIDTH, VEHICLE_IMAGE_HEIGHT)
    ctx.translate(VEHICLE_IMAGE_WIDTH / 2, VEHICLE_IMAGE_HEIGHT / 2)
    ctx.rotate(Math.PI)
    ctx.translate(-VEHICLE_IMAGE_WIDTH / 2, -VEHICLE_IMAGE_HEIGHT / 2)
    ctx.drawImage(img, 0, 0, VEHICLE_IMAGE_WIDTH, VEHICLE_IMAGE_HEIGHT)
    URL.revokeObjectURL(url)

    return ctx.getImageData(0, 0, VEHICLE_IMAGE_WIDTH, VEHICLE_IMAGE_HEIGHT)
}

export async function registerVehicleImages(map: maplibregl.Map, colors: string[]) {
    const template = await fetchVehicleSvg()
    for (const color of colors) {
        const id = `vehicle-${colorToImageId(color)}`
        if (map.hasImage(id)) continue
        const coloredSvg = renderVehicleSvg(template, color)
        const imageData = await svgToImageData(coloredSvg)
        map.addImage(id, imageData, { pixelRatio: 2 })
    }
}

export function useLayerData(layer: string, data?: object, map, mapLoaded) {
    useEffect(() => {
        if (!mapLoaded || !data) return

        map!.current!.getSource(layer)!.setData(data)

        // Clear the map when leaving this screen
        return () => {
            if (map!.current!.getSource(layer))
                map!.current!.getSource(layer)!.setData({
                    type: 'FeatureCollection',
                    features: [],
                })
        }
    }, [map, mapLoaded, data, layer])
}

export function formatMillisTime(time) {
    return DateTime.fromMillis(time).setZone('Europe/Ljubljana').toFormat('HH:mm')
}

/**
 * Format a millisecond timestamp for the navigator.
 * 'relative' shows "X min" for times within 60 min; falls back to HH:mm for far/past times.
 * 'absolute' always shows HH:mm.
 */
export function formatNavTime(millis: number, format: FormatType): string {
    if (format === 'absolute') return formatMillisTime(millis)
    const diffMin = Math.round((millis - Date.now()) / 60000)
    if (diffMin > 60 || diffMin < 0) return formatMillisTime(millis)
    if (diffMin === 0) return 'Now'
    return `${diffMin} min`
}

export function decimalFormat(value: number) {
    return value.toFixed(2).replace('.', ',')
}

export function imgProxy(url: string, size: number = 128) {
    return `https://img.tomato.derp.si/insecure/s:${size}/plain/${url}`
}

export function mapSetPaintProperty(map: maplibregl.Map, layerPrefix: string, property: string, value: DataDrivenPropertyValueSpecification<unknown> | ExpressionSpecification) {
    map.getStyle().layers.forEach((layer) => {
        if (layer.id.startsWith(layerPrefix)) {
            map.setPaintProperty(layer.id, property, value)
        }
    })
}

export function mapSetLayoutProperty(map: maplibregl.Map, layerPrefix: string, property: string, value: DataDrivenPropertyValueSpecification<unknown> | ExpressionSpecification) {
    map.getStyle().layers.forEach((layer) => {
        if (layer.id.startsWith(layerPrefix)) {
            map.setLayoutProperty(layer.id, property, value)
        }
    })
}

export function ensureVehicleLayersOnTop(map: maplibregl.Map) {
    const vehicleLayers = ['vehicles', 'planVehicleLocations']
    for (const layerId of vehicleLayers) {
        if (map.getLayer(layerId)) {
            map.moveLayer(layerId)
        }
    }
}

export function downloadStringAsFile(text: string, fileType: string, fileName: string) {
    const blob = new Blob([text], { type: fileType })
    const a = document.createElement('a')
    a.download = fileName
    a.href = URL.createObjectURL(blob)
    a.dataset.downloadurl = [fileType, a.download, a.href].join(':')
    a.style.display = 'none'
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    setTimeout(() => URL.revokeObjectURL(a.href), 1500)
}

/**
 * Extracts the route key from a transit item.
 * Uses route_short_name if available, otherwise falls back to trip_headsign.
 *
 * @param {Arrival | { route_short_name?: string, trip_headsign?: string }} item - The transit item (arrival, schedule, etc.)
 * @returns {string | null} - The route key or null if neither is available
 */
export function getRouteKey(item: Arrival | { route_short_name?: string, trip_headsign?: string | null }): string | null {
    return item.route_short_name || (typeof item.trip_headsign === 'string' ? item.trip_headsign : null) || null
}

/**
 * Checks if a route should be displayed based on selected routes filter.
 *
 * @param {Arrival | { route_short_name?: string, trip_headsign?: string }} item - The transit item (arrival, schedule, etc.)
 * @param {Set<string>} selectedRoutes - Set of selected route keys
 * @returns {boolean} - True if the route is selected or if no filter is applied
 */
export function isRouteSelected(item: Arrival | { route_short_name?: string, trip_headsign?: string | null }, selectedRoutes?: Set<string>): boolean {
    if (!selectedRoutes) return true
    const routeKey = getRouteKey(item)
    return routeKey !== null && selectedRoutes.has(routeKey)
}
